import { rankWith, uiTypeIs } from '@jsonforms/core';
import { withJsonFormsControlProps } from '@jsonforms/react';
import { TextInput } from '../../../../../controls/TextInput';
import { FormItem } from '../../../../../controls/FormItem';
import { FieldProps, WithDynamicFormContext } from '../WithDynamicFormContext';
import './style.scss';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { formatPhoneNumber } from '../../../../../utils/phoneNumberFormat';
import { debounce } from 'lodash';

type ValueType = {
  value: string;
  valueFormate?: string;
  focus?: boolean;
};

const TextControl = ({
  path,
  data,
  handleChange,
  handleRemove,
  handleBlur,
  handleFocused,
  label,
  required,
  schema,
  disabled,
  uischema,
  optionalMessage,
}: FieldProps) => {
  const upperCase = uischema.options?.upperCase;
  const moreInfo = uischema.options?.moreInfo;
  const dateFormat = uischema.options?.toDateFormat;
  const removedPaths = uischema.options?.removedPaths;
  const dependentPath = uischema.options?.dependentPath;
  const useDataPath = uischema.options?.useDataPath;
  const phoneFormat = uischema.options?.phoneFormat;
  const [valueState, setValueState] = useState<ValueType>();

  const textData = data
    ? dateFormat
      ? moment(data).format(dateFormat)
      : data
    : '';

  const debouncedChange = debounce(async (val: string) => {
    if (!!val) {
      await handleChange(path, upperCase ? val.toUpperCase() : val);
      if (useDataPath) handleChange(useDataPath, false, true);
    } else {
      handleRemove(path);
    }
    if (removedPaths) {
      removedPaths.forEach((removedPath: string) => {
        handleRemove(removedPath);
      });
    }
  }, 500);

  useEffect(() => {
    setValueState({
      value: textData,
      valueFormate:
        phoneFormat && !valueState?.focus && !valueState?.valueFormate
          ? formatPhoneNumber(textData)
          : undefined,
      focus: valueState?.focus,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (textData === valueState?.value) return;
    debouncedChange(valueState?.value!);

    return () => {
      debouncedChange.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueState?.value]);

  return (
    <FormItem
      label={label as string}
      required={required}
      classes="tru-text-input-control"
      moreInfo={moreInfo}
      optionalMessage={optionalMessage}
    >
      <TextInput
        onBlur={() => {
          handleBlur(path);
          if (phoneFormat)
            setValueState({
              value: valueState?.value!,
              valueFormate: formatPhoneNumber(valueState?.value!),
              focus: false,
            });
        }}
        onFocus={() => {
          if (phoneFormat)
            setValueState({
              value: valueState?.value!,
              valueFormate: undefined,
              focus: true,
            });
          handleFocused?.();
        }}
        value={
          valueState?.valueFormate
            ? valueState?.valueFormate!
            : valueState?.value
        }
        disabled={disabled}
        onChange={(value) => {
          setValueState({ value, focus: true })
          if (!!value) {
            handleChange(path, upperCase ? value.toUpperCase() : value);
            if (dependentPath)
              dependentPath.forEach((itemPath : any) => {
                handleChange(itemPath, value);
              })
          } else {
            if (dependentPath)
              dependentPath.forEach((itemPath : any) => {
                handleRemove(itemPath);
              })
            handleRemove(path);
          }
          if (removedPaths) {
            removedPaths.forEach((removedPath: string) => {
              handleRemove(removedPath);
            });
          }
        }}
      />
      {schema.description && (
        <p className="description margin-bottom">{schema.description}</p>
      )}
    </FormItem>
  );
};

export const TextInputControlEntry = {
  tester: rankWith(2, uiTypeIs('Control')),
  renderer: withJsonFormsControlProps(WithDynamicFormContext(TextControl)),
};
